import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnDestroy,
  OnInit,
  HostListener,
  Type,
  ViewChild
} from '@angular/core';
import {Location} from '@angular/common';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';
import {TimerObservable} from 'rxjs/observable/TimerObservable';
import 'rxjs/add/operator/takeWhile';

import {UserService} from '../core/ecv-http-services/services/user.service';
import {CvsComponent} from '../cvs/cvs.component';
import {EducationComponent} from '../education/education.component';
import {ExperienceComponent} from '../experience/experience.component';
import {FinishEcvComponent} from '../finish-ecv/finish-ecv.component';
import {NetworkComponent} from '../network/network.component';
import {PersonalDataComponent} from '../personal-data/personal-data.component';
import {CustomValidators} from '../core/validator/custom-validators';
import {ValidatorService} from '../core/validator/validator.service';
import {AlertService} from '../widgets/alert/alert.service';
import {FlowDirective} from './flow.directive';
import {NgRedux} from 'ng2-redux';
import {IAppState, INITIAL_STATE} from '../store';
import {SEARCHED_REF_NUMBER} from '../actions';
import {Observable} from 'rxjs/Observable';
import {ScalarObservable} from 'rxjs/observable/ScalarObservable';

@Component({
  selector: 'app-flow',
  templateUrl: './flow.component.html',
  styleUrls: ['./flow.component.css'],
  entryComponents: [CvsComponent, PersonalDataComponent, ExperienceComponent, EducationComponent,
    NetworkComponent, FinishEcvComponent]
})
export class FlowComponent implements AfterViewInit, OnInit, OnDestroy {

  userSearchForm: FormGroup;
  tabIndexes: number[];
  activeIndex: number;
  showCvs: boolean;
  showSpecialistData: boolean;
  refNumber: string;
  userId: number;
  @ViewChild(FlowDirective) adHost: FlowDirective;
  @ViewChild('cvs') cvs: CvsComponent;
  private stepTypes: Type<any>[];
  private componentRefs: ComponentRef<any>[] = [null, null, null, null, null];
  private alive: boolean;
  private interval = 180000;
  windowHeight: number;
  windowWidth: number;
  windowYOffset: number;
  userFound = false;

  constructor(private componentFactoryResolver: ComponentFactoryResolver, private translateService: TranslateService,
              private alertService: AlertService, private userService: UserService, private validatorService: ValidatorService,
              private route: ActivatedRoute, private location: Location, private ngRedux: NgRedux<IAppState>) {
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;
    this.windowYOffset = window.pageYOffset;
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    this.windowYOffset = window.pageYOffset;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize($event) {
    this.windowWidth = $event.target.innerWidth;
    this.windowHeight = $event.target.innerHeight;
  }

  private static loadStepTypes() {
    return [
      PersonalDataComponent,
      EducationComponent,
      ExperienceComponent,
      NetworkComponent,
      FinishEcvComponent
    ];
  }

  ngOnInit(): void {
    this.stepTypes = FlowComponent.loadStepTypes();
    this.tabIndexes = Array(this.stepTypes.length).fill(0).map((x, i) => i);
    this.alive = true;
    //this.enableAutoSave();
    this.createForm();
    this.showSpecialistData = false;

    this.route.params.subscribe(params => {
      this.initProperStep(params);
      const refNumberFromUrl = params['refNumber'];
      if (refNumberFromUrl !== undefined) {
        this.refNumber = refNumberFromUrl;
      } else {
        const lastSearchedRefNumber = this.ngRedux.getState().searchedRefNumber;
        if (lastSearchedRefNumber !== undefined) {
          this.refNumber = lastSearchedRefNumber;
        }
      }
      this.userFound = this.refNumber !== undefined;
    });
  }

  ngOnDestroy(): void {
    this.alive = false;
  }

  ngAfterViewInit(): void {
    if (this.refNumber) {
      this.searchForExpert();
    }
  }

  createForm() {
    this.userSearchForm = new FormGroup({
      'ref-number-search': new FormControl('', [CustomValidators.notEmpty,
        CustomValidators.validateReferenceNumber()])
    });
  }

  public searchForExpert() {
    this.validatorService.validate(this.userSearchForm);
    if (!this.validatorService.isValid(this.userSearchForm)) {
      return;
    }
    this.userService.checkIfExpertExists(this.refNumber).subscribe(
      response => {
        const expertId = response.userId;
        this.userId = expertId;
        this.loadStepsData(expertId);
        this.loadCvs(expertId);
        this.updatePath();
        this.showSpecialistData = true;
        this.userFound = true;
        this.ngRedux.dispatch({type: SEARCHED_REF_NUMBER, value: this.refNumber});
      }, error => {
        this.ngRedux.dispatch({type: SEARCHED_REF_NUMBER, value: undefined});
        this.showSpecialistData = false;
        this.userFound = false;
        this.updatePath();
      });
  }

  public changeActiveIndex(activeIndex: number, disablePathChange?: boolean) {
    this.activeIndex = activeIndex;
    this.loadComponent();

    if (!disablePathChange) {
      this.updatePath();
    }
  }

  public nextStep() {
    if (this.activeIndex < this.tabIndexes.length - 1) {
      this.changeActiveIndex(this.activeIndex + 1);
    }
  }

  public prevStep() {
    if (this.activeIndex > 0) {
      this.changeActiveIndex(this.activeIndex - 1);
    }
  }

  public saveAllStepsData(): void {
    const allObservables: Observable<any>[] = [];
    const scalarObservables: Observable<any>[] = [];
    let onSaveObservable;
    let scalar;
    if (this.userFound) {
      for (const component of this.componentRefs) {
        if (component) {
          const onSaveResult = component.instance.onSave();
          if (onSaveResult !== undefined) {
            if (onSaveResult instanceof ScalarObservable && onSaveResult.value === false) {
              scalar = onSaveResult;
              scalarObservables.push(scalar);
              break;
            } else {
              onSaveObservable = onSaveResult;
              allObservables.push(onSaveObservable);
            }
          }
        }
      }
    }
    if (scalarObservables.length > 0) {
      Observable.forkJoin(scalarObservables).subscribe(() => {
        this.showEcvAlert(scalar);
      });
    } else {
      Observable.forkJoin(allObservables).subscribe(() => {
        this.showEcvAlert(onSaveObservable);
      });
    }
  }

  public saveCurrentDataStep(): void {
    if (this.userFound) {
      const onSaveObservable = this.componentRefs[this.activeIndex].instance.onSave();
      onSaveObservable.subscribe(() => {
        this.showEcvAlert(onSaveObservable);
      }, error => {
        this.showEcvAlert(onSaveObservable);
      });
    }
  }

  private showEcvAlert(valid: Observable<any>) {
    if (valid instanceof ScalarObservable && valid.value === false) {
      this.translateService.get('ALERT.FIX_ERRORS', {}).subscribe((res: string) => {
        this.alertService.error(res);
      });
    } else {
      this.translateService.get('ALERT.ECV_SAVED', {}).subscribe((res: string) => {
        this.alertService.success(res);
      });
    }
  }

  private initProperStep(routeParams): void {
    const step = this.parseStepParam(routeParams['step']);
    if (step) {
      // on init with proper step parameter, path change must be disabled for proper back/forward button handling in browser
      this.changeActiveIndex(step, true);
    } else {
      this.changeActiveIndex(0);
    }
  }

  private parseStepParam(stepParam): number {
    if (this.regexMatches(stepParam)) {
      const newIndex = Number(stepParam.split('-')[1]) - 1;
      if (newIndex < this.tabIndexes.length && newIndex >= 0) {
        return newIndex;
      }
    }
    return null;
  }

  private regexMatches(stepParam): boolean {
    return /^step-[0-9]+$/.test(stepParam);
  }

  private updatePath(): void {
    let path = '/ecv/step-' + (this.activeIndex + 1);
    if (this.refNumber) {
      path += '/' + this.refNumber;
    }
    this.location.go(path);
  }

  private enableAutoSave() {
    TimerObservable.create(this.interval, this.interval)
      .takeWhile(() => this.alive)
      .subscribe(() => {
        this.saveAllStepsData();
      });
  }

  private loadComponent() {
    this.adHost.viewContainerRef.detach();
    const componentInstance = this.findInstantiatedComponent();
    if (componentInstance != null) {
      this.adHost.viewContainerRef.insert(componentInstance.hostView);
    } else {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.stepTypes[this.activeIndex]);
      const componentRef = this.adHost.viewContainerRef.createComponent(componentFactory);
      this.componentRefs[this.activeIndex] = componentRef;
      if (this.userFound) {
        componentRef.instance.loadData(this.userId);
        componentRef.instance.saveForm.subscribe(event => {
          this.saveCurrentDataStep();
        });
      }
    }
  }

  private findInstantiatedComponent() {
    for (const entry of this.componentRefs) {
      if (entry && entry.componentType === this.stepTypes[this.activeIndex]) {
        return entry;
      }
    }
    return null;
  }

  private loadStepsData(userId) {
    for (const component of this.componentRefs) {
      if (component) {
        component.instance.loadData(userId);
        component.instance.saveForm.subscribe(event => {
          this.saveCurrentDataStep();
        });
      }
    }
  }

  private loadCvs(userId: number) {
    if (this.showCvs) {
      this.cvs.loadCvs(userId);
    }
  }

  public scrollYPosition() {
    if (this.windowWidth < 992) {
      return 0;
    }
    return Math.max(this.windowYOffset - 100, 0);
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/flow/flow.component.ts